import { type QueryKey, type QueryObserverOptions, useQuery } from '@tanstack/react-query';
import { receivableInvoicesApiNamespace } from 'receivable-invoices-js/constants/hosts';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import { type ClientPortalAPI } from 'receivable-invoices-js/react/api/models/client-portal';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';

interface Params {
  clientId: string;
}

interface QueryResponse {
  data: ClientPortalAPI;
}

interface CustomOptions {
  onError?: (error: unknown) => void;
}

const URL = `${receivableInvoicesApiNamespace}/receivable_invoices/client_portals/shared`;

export function fetchClientPortalKey(clientId: string): QueryKey {
  return ['client-portal', clientId] as QueryKey;
}

export async function fetchClientPortal(params: Params, customOptions: CustomOptions) {
  const { onError } = customOptions;

  try {
    const response = await qontoClient<QueryResponse>(`${URL}/${params.clientId}`);

    const normalizedResponse = {
      ...response.data.attributes,
    };

    return camelizeKeys(normalizedResponse);
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
}

export type FetchClientPortalResponse = Awaited<ReturnType<typeof fetchClientPortal>>;

export function useFetchClientPortal(
  params: Params,
  customOptions?: CustomOptions,
  queryOptions?: Partial<QueryObserverOptions<FetchClientPortalResponse>>
) {
  return useQuery({
    queryKey: fetchClientPortalKey(params.clientId),
    queryFn: () => fetchClientPortal(params, { ...customOptions }),
    ...queryOptions,
  });
}
